"use client";

import React, { useState } from "react";
import { cn } from "utils";
import { Icon } from "@ospweb/sdk-ui";
import { Heatmap, type HeatmapNode } from "@/src/components/Heatmap";
import { useThemesHeatmapData } from "@/src/hooks";
import { formatBigNumber } from "@/src/utils";
import { ROUTER_PATH } from "@/src/config/router";
import { ViewDetailsLink } from "./ViewDetailsLink";

interface ThemeHeatmapBusinessChartProps {
  type: "stock" | "crypto";
  stockType?: "US" | "HK";
}

const formatTooltipChange = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  const absPercent = Math.abs(value);
  return value === 0
    ? "0%"
    : `${formatBigNumber(absPercent, { needFormat: true, trimZeros: true, precision: 2 })}%`;
};

export const ThemeHeatmapBusinessChart: React.FC<
  ThemeHeatmapBusinessChartProps
> = ({ type, stockType }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: nodes = [], isLoading } = useThemesHeatmapData(type, {
    enabled: true,
    stockType: type === "stock" ? stockType : undefined,
  });

  const renderTooltip = (node: HeatmapNode) => {
    const change = node.changePercent24h;
    const isUp = (change ?? 0) > 0;
    const isDown = (change ?? 0) < 0;
    const changeClass = cn(
      "body_medium_xs text-[7.5px] font-medium flex items-center gap-[1px]",
      isUp && "text-content_accent1_primary",
      isDown && "text-content_accent2_primary",
      !isUp && !isDown && "text-content_secondary",
    );
    return (
      <div className="rounded-1 bg-background_solid_tertiary px-[8px] py-[6px] min-w-[96px] min-h-[33px] border border-border_white_transparent10">
        <div className="body_medium_s text-content_primary mb-[2px] whitespace-nowrap">
          {node.name}
        </div>
        <div className={changeClass}>
          {isUp && (
            <Icon
              name="upvote"
              size={7.5}
              className="text-content_accent1_primary"
            />
          )}
          {isDown && (
            <Icon
              name="downvote"
              size={7.5}
              className="text-content_accent2_primary"
            />
          )}
          <span className="whitespace-nowrap">
            {formatTooltipChange(change)}
          </span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-[320px] w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  if (!nodes.length) {
    return null;
  }

  const discoverHref =
    type === "crypto"
      ? ROUTER_PATH.DISCOVER_CRYPTO
      : stockType === "HK"
        ? ROUTER_PATH.DISCOVER_HK_STOCKS
        : ROUTER_PATH.DISCOVER_US_STOCKS;

  return (
    <>
      <div className="w-full overflow-hidden">
        <Heatmap
          items={nodes}
          className="h-[320px]"
          selectedId={selectedId}
          onSelect={(node) => setSelectedId(node.id)}
          renderTooltip={renderTooltip}
          config={{
            compressSmallCells: true,
            minHeightForTwoLines: 36,
            minAreaRatio: 0.018,
            colorConfig: { borderWidth: 1, borderColor: "#ffffff" },
          }}
        />
      </div>
      <ViewDetailsLink href={discoverHref} />
    </>
  );
};
